import {
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import { isArray } from "lodash";
import ValueList from "~/components/common/forms/ValueList";
import {
  CreateProgramEligibilityQuestionMutationVariables as Question,
  EligibilityQuestionAnswerFormatTypeEnum as AnswerFormatType,
  EligibilityQuestionAnswerModuleEnum as AnswerModule,
} from "~/graphql/__generated__/graphql";

import { ANSWER_MODULE_REGISTRY } from "@caredna-marlin/utils/answerModules/registry"

type Props = {
  local: Question,
  setLocal: (q: Question) => void,
  lockFormat?: boolean,
  loading: boolean,
}

const formats: Record<AnswerFormatType, string> = {
  boolean: "Yes/No",
  selected_option: "Select option",
  multi_select_options: "Select multiple",
  free_text: "Free text",
  numeric: "Numeric",
}

export const answerFormats = Object.keys(formats).map((format, i) => ({ key: i, format, label: formats[format as AnswerFormatType] }))
export const answerModules = Object.values(AnswerModule).map(module => ({
  key: module,
  label: ANSWER_MODULE_REGISTRY[module].label
}));

export const predicateValid = (question: Question) => {
  if (!question.answerModule) { return true; }

  try {
    ANSWER_MODULE_REGISTRY[question.answerModule].parsePredicate(question.answerModulePredicate);
    return true;
  } catch (e) {
    return false
  }
}

const labelSize = { xs: 12, md: 5, lg: 4 };
const inputSize = { xs: 12, md: 7, lg: 8 };

const QuestionForm = ({ local, setLocal, lockFormat, loading }: Props) => {
  const usesPredicate = !!local.answerModule && !!ANSWER_MODULE_REGISTRY[local.answerModule].examplePredicate
  const predicateError = !!local.answerModule && !predicateValid(local)
  const usesAnswerOptionList = local.answerFormatType === "selected_option" || local.answerFormatType === "multi_select_options";
  const answerOptions = isArray(local.answerOptions)
    ? local.answerOptions
    : local.answerOptions ? [local.answerOptions] : [];
  const programEligibilityQuestions = isArray(local.programEligibilityQuestions)
    ? local.programEligibilityQuestions
    : [local.programEligibilityQuestions];

  return <Grid container sx={{ alignItems: "center" }} rowSpacing={1}>
    <Grid size={labelSize}>
      Question
    </Grid>
    <Grid size={inputSize}>
      <TextField
        autoFocus
        size="small"
        type="text"
        required
        fullWidth
        value={local.question}
        onChange={(e) => setLocal({ ...local, question: e.target.value })}
        disabled={loading}
      />
    </Grid>

    <Grid size={labelSize}>
      Answer Format
    </Grid>
    <Grid size={inputSize}>
      <Select
        size="small"
        fullWidth
        value={local.answerFormatType}
        onChange={(e) => setLocal({
          ...local,
          answerFormatType: e.target.value as AnswerFormatType,
          answerModule: null,
        })}
        disabled={lockFormat || loading}
      >
        {answerFormats.map(format => <MenuItem key={format.key} value={format.format}>{format.label}</MenuItem>)}
      </Select>
    </Grid>

    {usesAnswerOptionList && <Paper sx={{ width: "100%", p: 2, mb: 2 }}>
      <InputLabel>Options</InputLabel>
      <ValueList
        valueProp="answer"
        value={answerOptions}
        onChange={answerOptions => setLocal({ ...local, answerOptions })}
      />
    </Paper>}

    <Grid size={labelSize}>
      Answer Module
    </Grid>
    <Grid size={inputSize}>
      <Select
        size="small"
        fullWidth
        value={local.answerModule ?? "null"}
        onChange={(e) => setLocal({ ...local, answerModule: e.target.value === "null" ? null : e.target.value as AnswerModule })}
        disabled={loading}
      >
        <MenuItem value="null">Not automatically answered</MenuItem>
        {answerModules
          .filter(module => ANSWER_MODULE_REGISTRY[module.key].answerModuleType === local.answerFormatType)
          .map(module => module && <MenuItem key={module.key} value={module.key}>{module.label}</MenuItem>)}
      </Select>
    </Grid>

    {usesPredicate && <>
      <Grid size={labelSize}>
        Answer Predicate
      </Grid>
      <Grid size={inputSize}>
        <TextField
          size="small"
          type="text"
          fullWidth
          value={local.answerModulePredicate ?? ""}
          onChange={(e) => setLocal({ ...local, answerModulePredicate: e.target.value ? e.target.value : null })}
          disabled={loading}
          helperText={"Example: " + ANSWER_MODULE_REGISTRY[local.answerModule!].examplePredicate}
          error={predicateError}
        />
      </Grid>
    </>}

    <Grid size={labelSize}>
      Disqualifying Answers
    </Grid>
    <Grid size={inputSize}>
      <TextField
        size="small"
        type="text"
        fullWidth
        value={programEligibilityQuestions.length
          ? `${programEligibilityQuestions.length} program configuration${programEligibilityQuestions.length === 1 ? "" : "s"}`
          : "None"
        }
        disabled={true}
      />
    </Grid>
  </Grid>
}

export default QuestionForm;
